import React from 'react';
import { Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { UrlRule, NestedRuleGroup as NestedRuleGroupType } from '../types';
import UrlRuleForm from './UrlRuleForm';
import { LogicRadioGroup } from './StyledRadioButtons';

interface NestedRuleGroupProps {
  group: NestedRuleGroupType;
  onUpdate: (groupId: string, updates: Partial<NestedRuleGroupType>) => void;
  onRemove: (groupId: string) => void;
  onAddCondition: (groupId: string) => void;
  onAddGroup: (groupId: string) => void;
  onUpdateRule: (groupId: string, ruleId: string, field: keyof UrlRule, value: any) => void;
  onRemoveRule: (groupId: string, ruleId: string) => void;
  level?: number;
}

const NestedRuleGroupComponent: React.FC<NestedRuleGroupProps> = ({
  group,
  onUpdate,
  onRemove,
  onAddCondition,
  onAddGroup,
  onUpdateRule,
  onRemoveRule,
  level = 0,
}) => {
  const isNestedGroup = level > 0;
  const indent = level * 16;

  const renderRule = (rule: UrlRule | NestedRuleGroupType) => {
    if ('rules' in rule) {
      return (
        <NestedRuleGroupComponent
          key={rule.id}
          group={rule}
          onUpdate={onUpdate}
          onRemove={onRemove}
          onAddCondition={onAddCondition}
          onAddGroup={onAddGroup}
          onUpdateRule={onUpdateRule}
          onRemoveRule={onRemoveRule}
          level={level + 1}
        />
      );
    } else {
      return (
        <UrlRuleForm
          key={rule.id}
          urlRule={rule}
          onUpdate={(ruleId, field, value) => onUpdateRule(group.id, ruleId, field, value)}
          onRemove={(ruleId) => onRemoveRule(group.id, ruleId)}
        />
      );
    }
  };

  return (
    <div
      style={{
        marginLeft: isNestedGroup ? 46 : indent,
        border: isNestedGroup ? '1px solid #e8e8e8' : 'none',
        borderRadius: isNestedGroup ? 8 : 0,
        padding: isNestedGroup ? 16 : 0,
        backgroundColor: 'white',
        marginBottom: 16,
        paddingLeft: level > 0 ? 8 : 0,
      }}
    >
      {isNestedGroup && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Logic:</span>
            <LogicRadioGroup
              value={group.type}
              onChange={(value) => onUpdate(group.id, { type: value })}
            />
          </div>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onRemove(group.id)}
            size="small"
          >
            Remove Group
          </Button>
        </div>
      )}

      {!isNestedGroup && group.id !== 'url-root' && (
        <div style={{ marginBottom: 16 }}>
          <span style={{ marginRight: 8, fontSize: '14px', fontWeight: '500' }}>Logic:</span>
          <LogicRadioGroup
            value={group.type}
            onChange={(value) => onUpdate(group.id, { type: value })}
            size="small"
          />
        </div>
      )}

      {group.rules.map((rule, index) => (
        <React.Fragment key={rule.id}>
          {index > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                margin: '8px 0',
                marginLeft: 46,
              }}
            >
              <span style={{ fontSize: '12px', fontWeight: '500', color: 'rgba(76, 76, 76, 1)' }}>
                {group.type.toUpperCase()}
              </span>
            </div>
          )}
          {renderRule(rule)}
        </React.Fragment>
      ))}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <Button
          type="default"
          icon={<PlusOutlined />}
          onClick={() => onAddCondition(group.id)}
          style={{
            color: 'rgba(0, 70, 255, 1)',
            fontSize: '14px',
            fontWeight: 400,
            borderColor: 'rgba(179, 200, 255, 1)',
            display: 'inline-flex',
            alignItems: 'center',
            marginRight: 8,
          }}
          data-test-id="add-url-rule-button"
        >
          Condition
        </Button>
        <Button
          type="default"
          icon={<PlusOutlined />}
          onClick={() => onAddGroup(group.id)}
          style={{
            color: 'rgba(0, 70, 255, 1)',
            fontSize: '14px',
            fontWeight: 400,
            borderColor: 'rgba(179, 200, 255, 1)',
            display: 'inline-flex',
            alignItems: 'center',
          }}
          data-test-id="add-url-group-button"
        >
          Group
        </Button>
      </div>
    </div>
  );
};

export default NestedRuleGroupComponent;
